import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	UseQueryOptions,
	UseMutationOptions,
} from '@tanstack/react-query';
import { api } from '../../helpers/api';
import type { APIEndpoints } from '../../typings/api';

type EndpointConfig<
	TPath extends keyof APIEndpoints,
	TMethod extends string,
> = TPath extends keyof APIEndpoints
	? TMethod extends keyof APIEndpoints[TPath]
		? APIEndpoints[TPath][TMethod]
		: never
	: never;

type ExtractParams<T> = T extends { params: infer P } ? P : never;
type ExtractBody<T> = T extends { body: infer B } ? B : never;
type ExtractResponse<T> = T extends { response: infer R } ? R : never;

/**
 * Create a type-safe query hook from an API endpoint
 */
export function createQueryHook<
	TPath extends keyof APIEndpoints,
	TSelect = ExtractResponse<EndpointConfig<TPath, 'GET'>>,
>(
	path: TPath,
	queryKey: (
		params?: ExtractParams<EndpointConfig<TPath, 'GET'>>
	) => unknown[],
	defaultOptions?: Omit<
		UseQueryOptions<
			ExtractResponse<EndpointConfig<TPath, 'GET'>>,
			Error,
			TSelect
		>,
		'queryKey' | 'queryFn'
	>
) {
	return (
		params?: ExtractParams<EndpointConfig<TPath, 'GET'>>,
		options?: Omit<
			UseQueryOptions<
				ExtractResponse<EndpointConfig<TPath, 'GET'>>,
				Error,
				TSelect
			>,
			'queryKey' | 'queryFn'
		>
	) => {
		return useQuery({
			queryKey: queryKey(params),
			queryFn: () => api.get(path, params ? { params } : undefined),
			...defaultOptions,
			...options,
		});
	};
}

/**
 * Create a type-safe mutation hook from an API endpoint
 */
export function createMutationHook<
	TPath extends keyof APIEndpoints,
	TMethod extends 'POST' | 'PUT' | 'PATCH' | 'DELETE',
>(
	method: TMethod,
	path: TPath,
	invalidateKeys?: unknown[][],
	options?: Omit<
		UseMutationOptions<
			ExtractResponse<EndpointConfig<TPath, TMethod>>,
			Error,
			{
				data?: ExtractBody<EndpointConfig<TPath, TMethod>>;
				params?: ExtractParams<EndpointConfig<TPath, TMethod>>;
			}
		>,
		'mutationFn'
	>
) {
	return () => {
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: async (variables: {
				data?: ExtractBody<EndpointConfig<TPath, TMethod>>;
				params?: ExtractParams<EndpointConfig<TPath, TMethod>>;
			}): Promise<ExtractResponse<EndpointConfig<TPath, TMethod>>> => {
				const config: any = {};
				if (variables.data !== undefined) {
					config.data = variables.data;
				}
				if (variables.params !== undefined) {
					config.params = variables.params;
				}

				if (method === 'POST') {
					return api.post(path, config) as Promise<
						ExtractResponse<EndpointConfig<TPath, TMethod>>
					>;
				} else if (method === 'PUT') {
					return api.put(path, config) as Promise<
						ExtractResponse<EndpointConfig<TPath, TMethod>>
					>;
				} else if (method === 'PATCH') {
					return api.patch(path, config) as Promise<
						ExtractResponse<EndpointConfig<TPath, TMethod>>
					>;
				}
				return api.delete(path, config) as Promise<
					ExtractResponse<EndpointConfig<TPath, TMethod>>
				>;
			},
			onSuccess: () => {
				if (invalidateKeys) {
					invalidateKeys.forEach((key) => {
						queryClient.invalidateQueries({ queryKey: key });
					});
				}
			},
			...options,
		});
	};
}
